import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ConfirmDialog } from '../components/ui/Modal'

/**
 * "Form đang có thay đổi chưa lưu" — một cờ toàn cục để:
 *   1. `beforeunload`: đóng tab / F5 → browser hỏi.
 *   2. Điều hướng trong app (sidebar, nút Huỷ): `confirmLeave(fn)` hiện ConfirmDialog
 *      của mình thay vì `window.confirm`, và chỉ chạy `fn` khi user đồng ý.
 *
 * Admin dùng `<BrowserRouter>` nên không có `useBlocker` (chỉ data router). Không đổi
 * router chỉ vì việc này — cờ + confirm ở chỗ điều hướng là đủ và nhìn thấy được.
 */
interface DirtyApi {
  dirty: boolean
  setDirty: (v: boolean) => void
  /** Chạy `fn` ngay nếu không dirty; ngược lại hỏi trước. */
  confirmLeave: (fn: () => void) => void
}

const DirtyContext = createContext<DirtyApi | null>(null)

const MESSAGE = 'Bạn có thay đổi chưa lưu. Rời trang thì thay đổi sẽ mất.'

export function DirtyProvider({ children }: { children: React.ReactNode }) {
  const [dirty, setDirty] = useState(false)
  const [pending, setPending] = useState<(() => void) | null>(null)

  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      // Chrome cần returnValue để hiện hộp thoại (chuỗi bị bỏ qua, nhưng phải có).
      e.returnValue = MESSAGE
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  const confirmLeave = useCallback(
    (fn: () => void) => {
      if (!dirty) fn()
      else setPending(() => fn)
    },
    [dirty],
  )

  const api = useMemo<DirtyApi>(() => ({ dirty, setDirty, confirmLeave }), [dirty, confirmLeave])

  return (
    <DirtyContext.Provider value={api}>
      {children}
      <ConfirmDialog
        open={pending !== null}
        title="Rời trang?"
        message={MESSAGE}
        confirmLabel="Rời trang, bỏ thay đổi"
        onCancel={() => setPending(null)}
        onConfirm={() => {
          const fn = pending
          setPending(null)
          setDirty(false)
          fn?.()
        }}
      />
    </DirtyContext.Provider>
  )
}

export function useDirty(): DirtyApi {
  const ctx = useContext(DirtyContext)
  if (!ctx) throw new Error('useDirty phải nằm trong <DirtyProvider>')
  return ctx
}

/**
 * Form gọi hook này với trạng thái dirty của nó. Unmount (đã điều hướng đi) thì tự hạ cờ.
 */
export function useDirtyGuard(isDirty: boolean): void {
  const { setDirty } = useDirty()
  useEffect(() => {
    setDirty(isDirty)
    return () => setDirty(false)
  }, [isDirty, setDirty])
}
